// The mini app's surface credential, carried as a header instead of a cookie.
//
// `bo_surface` (helpers/surface/session.ts) is what lets the storefront's `/api/*` calls past the
// gate, and it normally rides as an HttpOnly cookie the page response sets. A phone that refuses
// cookies — Safari's "Block All Cookies", a locked-down in-app browser (LINE/IG/FB) — never keeps
// it, and then EVERY order call 401s while the catalogue keeps rendering (it comes from `/store/*`,
// which never passes that gate). The shopper sees a working shop that cannot open their order, and
// nothing on screen says why. This is the fallback that removes the cookie from the critical path.
//
// The token is stateless and HMAC-signed, so the header is exactly as trustworthy as the cookie —
// the gate verifies the signature either way and neither transport is believed on its own. Kept in
// memory only: no storage of any kind, so it works in the same locked-down browsers, and a page
// load simply asks again.
let pending = null;
let token = "";
let attempts = 0;
/**
 * How many times one page load will ask for the token before giving up.
 *
 * It used to ask ONCE and keep the failure forever: `pending` held the resolved "" and every later
 * call awaited that same empty answer, so a single dropped request during boot — an in-app browser
 * throttling the burst, a mobile network blinking on a QR scan — meant every `/api/*` call for the
 * whole visit went out with no credential and 401'd. On a phone that also keeps no cookie there is
 * no second line of defence, and the shopper got "this browser is blocking the shop" for what was
 * one lost request. Only the browser can retry it: the token is per page load and held in memory.
 */
const MAX_ATTEMPTS = 3;
/** Only the mini app has RULES server-side; every other surface has a real session already. */
function isSurfacePage() {
    try {
        const path = String(globalThis.location?.pathname ?? "");
        return path === "/miniapp" || path.startsWith("/miniapp/");
    }
    catch {
        return false;
    }
}
/**
 * The surface token for this page load, or "" when it isn't needed or can't be had.
 *
 * Fetched once per page load on the happy path (`pending` dedupes the burst of calls the store
 * fires on boot) and re-tried up to MAX_ATTEMPTS times while it keeps coming back empty. Never
 * throws: without it the cookie may still carry the request, and a failure here must not take down
 * a call that would otherwise work.
 */
export async function surfaceToken() {
    if (token)
        return token;
    if (!isSurfacePage())
        return "";
    if (!pending) {
        if (attempts >= MAX_ATTEMPTS)
            return "";
        attempts += 1;
        pending = fetch("/surface/token", { cache: "no-store" })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => String(d?.token ?? ""))
            .catch(() => "");
    }
    token = await pending;
    // Empty = this attempt failed. Drop the memo so the NEXT call re-asks rather than inheriting
    // the failure for the rest of the visit; `attempts` is what stops that becoming a retry storm
    // against a route that is genuinely gone.
    if (!token)
        pending = null;
    return token;
}
//# sourceMappingURL=surface.js.map